import { useState, useMemo } from 'react'
import { Box, Flex, Grid, Text, VStack } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { LuChevronRight as ChevronRight, LuMapPin as MapPin } from 'react-icons/lu'
import { AdvancedMarker, InfoWindow } from '@vis.gl/react-google-maps'
import { KMap, FitBounds } from '../../../lib/googleMaps'
import { ElementPin } from '../../../lib/mapMarkers'
import { brand, themeColors } from '../../../theme'
import { topSuppliers, supplierMiningLocations } from '../../../data/mockEstadisticas'

const statusColors: Record<string, string> = {
  Vigente: '#1d6664',
  'En Tramite': '#a8740e',
  Suspendido: '#b23a26',
}

const mineralColors: Record<string, string> = {
  Cobre: '#d49a2a',
  Oro: '#EAB308',
  Zinc: '#6366F1',
  Hierro: '#c8442f',
}

// Pin estándar de ubicación minera: color por mineral, icono de gema; el
// estado (vigente/trámite/suspendido) se muestra como punto en la esquina.
function MineralMarker({ mineral, status }: { mineral: string; status: string }) {
  const color = mineralColors[mineral] ?? '#857b71'
  const borderColor = statusColors[status] ?? '#857b71'
  return (
    <ElementPin
      kind="mineral"
      color={color}
      size={34}
      badge={<span style={{ display: 'block', width: 10, height: 10, borderRadius: '50%', background: borderColor, margin: 3 }} />}
    />
  )
}

const MotionBox = motion.create(Box)

export default function DetalleProveedoresPage() {
  const colors = themeColors
  const navigate = useNavigate()
  const maxTonelaje = Math.max(...topSuppliers.map((s) => s.tonelaje))
  const [hoveredSupplier, setHoveredSupplier] = useState<string | null>(null)
  const [hoveredLocation, setHoveredLocation] = useState<number | null>(null)

  const filteredLocations = hoveredSupplier
    ? supplierMiningLocations.filter((l) => l.supplierId === hoveredSupplier)
    : supplierMiningLocations
  const boundsPoints = useMemo(
    () => filteredLocations.map((l) => ({ lat: l.lat, lng: l.lng })),
    [filteredLocations],
  )

  return (
    <VStack gap={6} alignItems="stretch">
      {/* Breadcrumb */}
      <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <Flex gap={2} alignItems="center" mb={2}>
          <Text
            fontSize="sm"
            color={brand.green}
            cursor="pointer"
            _hover={{ textDecoration: 'underline' }}
            onClick={() => navigate('/app/estadisticas')}
          >
            Estadisticas
          </Text>
          <ChevronRight size={14} color={colors.textMuted} />
          <Text fontSize="sm" color={colors.text} fontWeight="600">
            Proveedores
          </Text>
        </Flex>
        <Text fontSize="2xl" fontWeight="bold" color={colors.text}>
          Detalle por Proveedor
        </Text>
        <Text fontSize="10px" textTransform="uppercase" letterSpacing="0.2em" fontWeight="bold" color={colors.textMuted} mt={1}>
          {topSuppliers.length} proveedores activos
        </Text>
      </MotionBox>

      {/* Mining Rights Map */}
      <MotionBox
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        bg={colors.bgCard}
        border="1px solid"
        borderColor={colors.border}
        borderRadius="2xl"
        overflow="hidden"
      >
        <Flex px={5} pt={4} pb={3} justifyContent="space-between" alignItems="center">
          <Box>
            <Flex alignItems="center" gap={2}>
              <MapPin size={18} color={brand.green} />
              <Text fontSize="md" fontWeight="bold" color={colors.text}>
                Ubicacion de Derechos Mineros
              </Text>
            </Flex>
            <Text fontSize="xs" color={colors.textMuted} mt={0.5}>
              {supplierMiningLocations.length} concesiones de {topSuppliers.length} proveedores
              {hoveredSupplier && (
                <Text as="span" color={brand.green} fontWeight="600">
                  {' '}— Filtrando: {supplierMiningLocations.find((l) => l.supplierId === hoveredSupplier)?.supplierName}
                </Text>
              )}
            </Text>
          </Box>
          <Flex gap={3}>
            {Object.entries(mineralColors).map(([mineral, color]) => (
              <Flex key={mineral} alignItems="center" gap={1}>
                <Box w="10px" h="10px" borderRadius="full" bg={color} />
                <Text fontSize="10px" color={colors.textMuted} fontWeight="600">{mineral}</Text>
              </Flex>
            ))}
          </Flex>
        </Flex>
        <Box h="380px">
          <KMap defaultCenter={{ lat: -12.0, lng: -75.0 }} defaultZoom={6} mapTypeId="hybrid">
            <FitBounds points={boundsPoints} padding={40} maxZoom={15} nonce={boundsPoints.length} />
            {filteredLocations.map((loc, i) => (
              <AdvancedMarker
                key={`${loc.supplierId}-${i}`}
                position={{ lat: loc.lat, lng: loc.lng }}
                onMouseEnter={() => setHoveredLocation(i)}
                onMouseLeave={() => setHoveredLocation((cur) => (cur === i ? null : cur))}
              >
                <MineralMarker mineral={loc.mineral} status={loc.status} />
              </AdvancedMarker>
            ))}
            {hoveredLocation != null && filteredLocations[hoveredLocation] && (
              <InfoWindow
                position={{ lat: filteredLocations[hoveredLocation].lat, lng: filteredLocations[hoveredLocation].lng }}
                pixelOffset={[0, -38]}
                disableAutoPan
                headerDisabled
              >
                <div style={{ minWidth: 180 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>{filteredLocations[hoveredLocation].supplierName}</div>
                  <div style={{ fontSize: 12, color: '#4a5568', marginBottom: 2 }}>{filteredLocations[hoveredLocation].miningRightName}</div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                      background: `${mineralColors[filteredLocations[hoveredLocation].mineral] ?? '#857b71'}22`,
                      color: mineralColors[filteredLocations[hoveredLocation].mineral] ?? '#857b71',
                    }}>{filteredLocations[hoveredLocation].mineral}</span>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                      background: `${statusColors[filteredLocations[hoveredLocation].status]}15`,
                      color: statusColors[filteredLocations[hoveredLocation].status],
                    }}>{filteredLocations[hoveredLocation].status}</span>
                  </div>
                </div>
              </InfoWindow>
            )}
          </KMap>
        </Box>
      </MotionBox>

      {/* Supplier Cards */}
      <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
        {topSuppliers.map((supplier, index) => (
          <MotionBox
            key={supplier.id}
            bg={colors.bgCard}
            border="1px solid"
            borderColor={hoveredSupplier === supplier.id ? brand.green : colors.border}
            borderRadius="2xl"
            p={5}
            cursor="pointer"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            boxShadow="none"
            _hover={{ borderColor: brand.green, transform: 'translateY(-2px)' }}
            onClick={() => navigate(`/app/estadisticas/proveedores/${supplier.id}`)}
            onMouseEnter={() => setHoveredSupplier(supplier.id)}
            onMouseLeave={() => setHoveredSupplier(null)}
          >
            <Flex justifyContent="space-between" alignItems="flex-start" mb={3}>
              <Box>
                <Text fontSize="md" fontWeight="bold" color={colors.text}>
                  {supplier.nombre}
                </Text>
                <Text fontSize="xs" color={colors.textMuted}>
                  RUC: {supplier.ruc}
                </Text>
              </Box>
              <Box
                bg={`${brand.green}22`}
                color={brand.green}
                px={2}
                py={0.5}
                borderRadius="md"
                fontSize="xs"
                fontWeight="600"
              >
                {supplier.mineralPrincipal}
              </Box>
            </Flex>
            <Flex justifyContent="space-between" alignItems="center" mb={2}>
              <Text fontSize="xl" fontWeight="bold" color={colors.text}>
                {supplier.tonelaje.toLocaleString()} TM
              </Text>
              <Text fontSize="sm" color={colors.textSecondary}>
                {supplier.porcentaje}%
              </Text>
            </Flex>
            {/* Mini bar */}
            <Box bg="#efe8dd" borderRadius="full" h="6px" overflow="hidden">
              <Box
                bg={brand.green}
                h="100%"
                borderRadius="full"
                width={`${(supplier.tonelaje / maxTonelaje) * 100}%`}
                transition="width 0.5s ease"
              />
            </Box>
          </MotionBox>
        ))}
      </Grid>
    </VStack>
  )
}
